"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Hammer, FileCheck2, Users, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const values = [{
  id: "craftsmanship",
  title: "Master craftsmanship",
  description: "Every joint, trim profile, and custom cabinet is built to endure and honor your home's historic lines.",
  iconName: "hammer"
}, {
  id: "transparency",
  title: "Open-book planning",
  description: "Clear cost breakdowns, predictable timelines, and structured communication from day one.",
  iconName: "file-check"
}, {
  id: "community",
  title: "Local stewardship",
  description: "We live and build in Decatur, protecting the character of Oakhurst, Winnona Park, and Great Lakes.",
  iconName: "users"
}];
export default function Story() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "hammer":
        return <Hammer className="size-5 text-chart-1" />;
      case "file-check":
        return <FileCheck2 className="size-5 text-chart-1" />;
      case "users":
        return <Users className="size-5 text-chart-1" />;
      default:
        return <Hammer className="size-5 text-chart-1" />;
    }
  };
  return <section id="our-story" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 border-b border-border text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          {/* Left Column: Narrative & Values */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col space-y-8">
            {/* Header / Intro */}
            <motion.div variants={itemVariants} className="space-y-4">
              <Badge variant="outline" className="bg-secondary text-secondary-foreground border-border uppercase tracking-widest text-xs font-semibold px-3 py-1">
                Our Story & Roots
              </Badge>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Built on local pride, precision, and architectural respect.
              </h2>
              <div className="h-0.5 w-16 bg-chart-1 rounded-full" />
            </motion.div>

            {/* Narrative text */}
            <motion.div variants={itemVariants} className="space-y-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
              <p>
                Decatur Heritage Renovations was founded with a singular commitment: to provide Decatur homeowners with a remodeling experience that blends architectural reverence with meticulous craftsmanship. We believe a historic bungalow or mid-century ranch should evolve without losing the soul that drew you to it in the first place.
              </p>
              <p>
                From structural additions in Oakhurst to bespoke kitchen rebuilds in Winnona Park, our team manages every detail under one roof. We balance honest cost planning with hands-on carpentry, treating every project as a lasting contribution to our shared neighborhood streetscapes.
              </p>
            </motion.div>

            {/* Core Values */}
            <motion.div variants={containerVariants} className="space-y-4 pt-2">
              <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                Principles that guide our work
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {values.map((val, i) => <motion.div key={val.id} data-index={i} variants={itemVariants} whileHover={prefersReducedMotion ? {} : {
                y: -4
              }} className="relative rounded-xl border border-border bg-card/85 backdrop-blur-md p-5 shadow-xs transition-[border-color,box-shadow] hover:border-chart-1/40 hover:shadow-md flex flex-col justify-between">
                    <div className="space-y-3">
                      <div className="inline-flex size-10 items-center justify-center rounded-lg bg-secondary border border-border">
                        {renderIcon(val.iconName)}
                      </div>
                      <h4 className="font-sans text-base font-semibold text-foreground leading-snug">
                        {val.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {val.description}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Call to action link */}
            <motion.div variants={itemVariants} className="pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ProcessPlanningPage" className="inline-flex items-center gap-2">
                  <span>Explore our process</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Column: Imagery & Studio Badge */}
          <motion.div initial={{
          opacity: prefersReducedMotion ? 1 : 0,
          y: prefersReducedMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-lg lg:max-w-none">
              {/* Main Team/Studio Photo */}
              <div className="relative overflow-hidden rounded-2xl border border-border shadow-lg bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_heritage_team_studio.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_heritage_team_studio.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_heritage_team_studio.png-wvc-sizes" alt="Decatur Heritage Renovations design and construction team collaborating in their studio" loading="lazy" decoding="async" className="w-full aspect-4/5 object-cover object-center" />
                {/* Subtle gradient overlay for contrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Floating frosted glass badge overlay */}
              <div className="absolute -bottom-6 -left-4 sm:left-6 right-4 sm:right-auto sm:max-w-xs rounded-xl border border-border bg-card/90 backdrop-blur-md p-5 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-1 bg-chart-1 rounded-full shrink-0" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Local Roots
                    </p>
                    <p className="font-serif text-lg font-semibold text-foreground">
                      20+ Years in Decatur
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Dedicated to neighborhood craft & lasting relationships.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}